import React, {useEffect, useState} from 'react';
import {createPortal} from 'react-dom';

import type {ReactNode} from 'react';


type Props = {
    /**
     * The id of DOM element to render content
     */
    holderId?: string,

    /**
     * Prevents further propagation of the current event in the capturing and bubbling phases
     */
    stopEvents?: boolean,

    /**
     * Any content you want to render inside the portal
     */
    children: ReactNode,
};
const defaultHolderId = 'portal-holder';

const stop = (e: {stopPropagation: () => void}) => {
    e.stopPropagation();
};

/**
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/components-portal}
 * */
function Portal(props: Props): ReturnType<typeof createPortal> | null {
    const {stopEvents, children} = props;
    const [portalHolder, setPortalHolder] = useState(document.getElementById(props.holderId ?? defaultHolderId));

    useEffect(() => {
        const holder = document.getElementById(props.holderId ?? defaultHolderId);
        if (holder) {
            setPortalHolder(holder);
        }
    }, [props.holderId]);

    if (portalHolder) {
        return createPortal(
            stopEvents ? (
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                <div
                    {...{
                        onClick: stop,
                        onContextMenu: stop,
                        onDoubleClick: stop,
                        onDrag: stop,
                        onDragEnd: stop,
                        onDragEnter: stop,
                        onDragExit: stop,
                        onDragLeave: stop,
                        onDragOver: stop,
                        onDragStart: stop,
                        onDrop: stop,
                        onMouseDown: stop,
                        onMouseEnter: stop,
                        onMouseLeave: stop,
                        onMouseMove: stop,
                        onMouseOver: stop,
                        onMouseOut: stop,
                        onMouseUp: stop,
                        onWheel: stop,
                        onKeyDown: stop,
                        onKeyPress: stop,
                        onKeyUp: stop,
                        onFocus: stop,
                        onBlur: stop,
                    }}>
                    {children}
                </div>
            ) : (
                <div>{children}</div>
            ),
            portalHolder,
        );
    }

    return null;
}

Portal.displayName = 'Portal';

export {Portal};
export default Portal;
